/**
 * SentinelBloom.ts
 * Rooted turret enemy for Starfall Courier — World 3+ (Brassclouds onward).
 *
 * Anchored to a platform tile. Cycles closed bud → open bloom → fire volley →
 * close. While closed, armoured petals shield the core from every angle and
 * a stomp from above bounces Nova harmlessly. While open (any of OPENING /
 * FIRING / CLOSING), a single stomp or shard hit kills it.
 *
 * On the OPENING → FIRING transition the bloom raises `fireRequested = true`
 * for exactly one tick. The level's combat code reads this flag each frame
 * and spawns the projectile pattern (pool wiring + pattern selection land
 * with the World 3 level bring-up). The bloom clears the flag at the top of
 * the next `update()` call.
 *
 * State machine: IDLE → OPENING → FIRING → CLOSING → IDLE (cycle)
 *                                                  ↘ HURT → DEAD (on hit)
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 3+ greybox stub. Wired into levels when W3 ships.
 * See ROADMAP.md Phase 4 (`SentinelBloom.ts`).
 *
 * @module SentinelBloom
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime } from '../../engine/gameState';
import { resolveAllPlatforms } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type SentinelBloomState = 'IDLE' | 'OPENING' | 'FIRING' | 'CLOSING' | 'HURT' | 'DEAD';

/** Width/height of the SentinelBloom hitbox in logical pixels. */
export const SENTINEL_BLOOM_WIDTH = 30;
export const SENTINEL_BLOOM_HEIGHT = 30;

/** Time spent as a closed bud between volleys (seconds). */
const IDLE_DURATION = 1.8;
/** Telegraph window before firing — petals visibly open (seconds). */
const OPENING_DURATION = 0.45;
/** Volley window — projectile spawned on entry (seconds). */
const FIRING_DURATION = 0.3;
/** Petals fold back into a closed bud (seconds). */
const CLOSING_DURATION = 0.45;
/** Hurt-flash duration before the dead flag flips (seconds). */
const HURT_DURATION = 0.25;

/** Render colour for greybox. */
export const SENTINEL_BLOOM_COLOR = '#c14b8c'; // magenta-bloom

// DECISION: Reusable hitbox to avoid per-tick allocations (mirrors the
// pattern in Nibbler / Shellback — same performance budget).
const _hitbox = { x: 0, y: 0, width: SENTINEL_BLOOM_WIDTH, height: SENTINEL_BLOOM_HEIGHT };

/**
 * Result of a projectile or stomp impact decision. Mirrors `ShellbackHitResult`
 * so the level combat code can branch on a single union across enemy types.
 *
 * DECISION: No `'KILLED'` value — `applyShardHit()` / `applyStomp()` never
 * set `dead = true` synchronously. A successful hit starts HURT → DEAD which
 * completes inside `update()` once the hurt timer elapses. Returning `'HURT'`
 * keeps the return value consistent with the post-call `state`.
 */
export type SentinelBloomHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/**
 * SentinelBloom — rooted, periodically-firing turret enemy.
 *
 * Spawn with `new SentinelBloom(x, y)`. Call `update(dt, level)` each fixed
 * tick. Read `fireRequested` after `update()` to spawn the volley exactly
 * once per cycle. Read `dead` to know when to remove from the scene.
 */
export class SentinelBloom extends BaseEntity {
  public readonly entityType = 'SentinelBloom';
  public readonly color = SENTINEL_BLOOM_COLOR;
  public state: SentinelBloomState = 'IDLE';

  /**
   * True once a hit has started the death sequence (`HURT`) or it has
   * finished (`DEAD`). See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'HURT' || this.state === 'DEAD';
  }
  /**
   * Single-tick fire pulse. Set true at the moment the bloom enters FIRING;
   * cleared at the top of the next `update()` call. Combat code reads it
   * once per tick after `update()` to spawn the volley.
   *
   * DECISION: Single-tick flag (no event queue, no callback) keeps the
   * entity ABI identical in shape to the rest of the enemy roster — the
   * scene/combat layer drives all cross-system effects.
   */
  public fireRequested = false;
  private _phaseTimer = IDLE_DURATION;

  constructor(x: number, y: number) {
    super(x, y, SENTINEL_BLOOM_WIDTH, SENTINEL_BLOOM_HEIGHT);
    // DECISION: vx stays 0 — the bloom is rooted to its tile.
    // Gravity still runs so a bloom spawned mid-air settles onto the
    // nearest platform on the first few ticks.
  }

  public update(dt: number, level: LevelRuntime): void {
    if (this.state === 'DEAD') return;

    // Consume any prior-tick fire pulse before we possibly re-arm it below.
    // DECISION: Cleared at the TOP of update() (not the bottom) so a combat
    // system that runs between consecutive enemy updates has a full tick to
    // observe the pulse before it expires.
    this.fireRequested = false;

    // ── Gravity + ground settle ────────────────────────────────────────────
    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);
    this.y += this.vy * dt;
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = r.x;
    this.y = r.y;
    if (r.landedOnTop) this.vy = 0;

    if (this.state === 'HURT') {
      this._phaseTimer -= dt;
      if (this._phaseTimer <= 0) {
        this.state = 'DEAD';
        this.dead = true;
        Logger.debug('SentinelBloom', 'Death after hurt');
      }
      return;
    }

    // ── Cycle phase transitions ────────────────────────────────────────────
    this._phaseTimer -= dt;
    if (this._phaseTimer > 0) return;

    switch (this.state) {
      case 'IDLE':
        this.state = 'OPENING';
        this._phaseTimer = OPENING_DURATION;
        Logger.debug('SentinelBloom', 'Opening');
        break;
      case 'OPENING':
        this.state = 'FIRING';
        this.fireRequested = true;
        this._phaseTimer = FIRING_DURATION;
        Logger.debug('SentinelBloom', 'Firing');
        break;
      case 'FIRING':
        this.state = 'CLOSING';
        this._phaseTimer = CLOSING_DURATION;
        Logger.debug('SentinelBloom', 'Closing');
        break;
      case 'CLOSING':
        this.state = 'IDLE';
        this._phaseTimer = IDLE_DURATION;
        Logger.debug('SentinelBloom', 'Idle');
        break;
    }
  }

  /**
   * True while the bloom is in any open phase (OPENING / FIRING / CLOSING)
   * and is therefore vulnerable to shards and stomps. Renderer also reads
   * this to pick the "open" sprite vs the closed bud.
   */
  public get isExposed(): boolean {
    return this.state === 'OPENING' || this.state === 'FIRING' || this.state === 'CLOSING';
  }

  /**
   * Apply one projectile hit. Petals shield from every angle while IDLE; the
   * open phases leave the core exposed and a single shard starts the death
   * sequence.
   *
   * @returns 'BLOCKED' if the petals absorbed the shot, 'HURT' on a hit that
   *          started the death sequence, 'IGNORED' if already dead/hurt.
   */
  public applyShardHit(): SentinelBloomHitResult {
    if (this.state === 'DEAD' || this.state === 'HURT') return 'IGNORED';
    if (!this.isExposed) {
      Logger.debug('SentinelBloom', 'Shard blocked by closed petals');
      return 'BLOCKED';
    }
    this._enterHurt();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. The closed bud is hard from above — stomping it
   * bounces Nova without damaging the bloom. While open, a stomp kills.
   *
   * @returns true if the stomp landed and started the death sequence; false
   *          if already dead/hurt or the petals were closed.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD' || this.state === 'HURT') return false;
    if (!this.isExposed) {
      Logger.debug('SentinelBloom', 'Stomp bounced off closed petals');
      return false;
    }
    this._enterHurt();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts) — delegates to
   * the specialised methods so `combatStep` can drive SentinelBloom through
   * the shared enemy interface.
   * @param fromStomp `true` for stomp contact, `false`/omitted for shard hits.
   * @returns `true` if this hit started the death sequence; `false` otherwise.
   */
  public applyHit(fromStomp = false): boolean {
    return fromStomp ? this.applyStomp() : this.applyShardHit() === 'HURT';
  }

  private _enterHurt(): void {
    this.state = 'HURT';
    this._phaseTimer = HURT_DURATION;
    this.fireRequested = false;
    Logger.debug('SentinelBloom', 'Hit — entering HURT');
  }
}
